/**
 * PasswordRevealedModal (F84) — muestra la password en CLARO una sola vez
 * después de crear/regenerar usuario. Botón para copiar al portapapeles.
 */
import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Copy, Eye, EyeOff } from "lucide-react";

import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { UserCreatedResponse } from "./schemas";

type Props = {
  cred: UserCreatedResponse | null;
  onClose: () => void;
};

export function PasswordRevealedModal({ cred, onClose }: Props) {
  const [show, setShow] = useState(true);
  const open = cred !== null;

  function copy() {
    if (!cred) return;
    navigator.clipboard.writeText(cred.password_plaintext);
    toast.success("Password copiada al portapapeles");
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setShow(true); onClose(); } }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle size={16} className="text-amber-500" />
            Password generada
          </DialogTitle>
          <DialogDescription>
            {cred?.mensaje}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <div className="text-[10px] uppercase text-muted-foreground mb-1">
              Usuario
            </div>
            <div className="rounded-md border bg-muted/30 px-3 py-2 font-mono text-sm">
              {cred?.user.email}
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase text-muted-foreground mb-1">
              Password (visible solo ahora)
            </div>
            <div className="flex items-center gap-1">
              <div className="flex-1 rounded-md border bg-muted/30 px-3 py-2 font-mono text-sm break-all">
                {show ? cred?.password_plaintext : "•".repeat(cred?.password_plaintext.length ?? 0)}
              </div>
              <Button
                type="button" variant="outline" size="icon"
                onClick={() => setShow((v) => !v)}
                title={show ? "Ocultar" : "Mostrar"}
              >
                {show ? <EyeOff size={14} /> : <Eye size={14} />}
              </Button>
              <Button type="button" variant="outline" size="icon" onClick={copy} title="Copiar">
                <Copy size={14} />
              </Button>
            </div>
          </div>

          <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-2 text-[11px] text-amber-700 dark:text-amber-400">
            ⚠ Esta password no se puede recuperar después. Si la pierdes,
            tendrás que regenerar otra desde el listado.
          </div>
        </div>

        <DialogFooter>
          <Button size="sm" onClick={onClose}>Entendido, ya la guardé</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
